import classNames from "classnames";
import { isStringValid, validateAndTrimString } from "core-lib/utils/utils";
import React from "react";
import useAutofocus from "ui-v2/hooks/useAutofocus";
import useScrollIntoView from "ui-v2/hooks/useScrollIntoView";
import { Typography } from "../Typography";
import DatePicker from "./DatePicker";
import s from "./Input.module.scss";
import Icon from "../Icon/Icon";

enum InputModes {
  text = "text",
  numeric = "numeric",
  email = "email",
  date = "date",
  search = "search",
  tel = "tel",
}
export enum RenderType {
  FormInput = "FormInput",
  StandAloneInput = "StandAloneInput",
}

export type InputTypes = keyof typeof InputModes;

type InputProps = React.HTMLProps<HTMLInputElement | HTMLTextAreaElement> & {
  inputType: InputTypes;
  label?: string;
  placeholder?: string;
  leftIcon?: React.JSX.Element;
  rightIcon?: React.JSX.Element;
  helperText?: string;
  showInputLength?: boolean;
  dateRenderFormat?: string;
  hasError?: boolean;
  errorText?: string;
  showRequiredAsterisk?: boolean;
  required?: boolean;
  isDisabled?: boolean;
  value?: string | Date;
  isTextarea?: boolean;
  scrollIntoView?: boolean;
  autoFocus?: boolean;
  limitCount?: number;
  inputRef?: React.MutableRefObject<
    HTMLInputElement | HTMLTextAreaElement | null
  >;
  containerClass?: string;
  inputClass?: string;
  handleChange?: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  >;
  renderType?: RenderType;
};

export function Input({
  label = "",
  helperText = "",
  errorText = "",
  value,
  inputType = "text",
  placeholder = "Enter input",
  leftIcon = null,
  rightIcon = null,
  inputRef = null,
  showInputLength = false,
  hasError = false,
  required = false,
  isDisabled = false,
  isTextarea = false,
  scrollIntoView = false,
  autoFocus = false,
  showRequiredAsterisk = false,
  containerClass = "",
  inputClass = "",
  limitCount = 0,
  onChange,
  dateRenderFormat = "DD/MM/YYYY",
  renderType = RenderType.FormInput,
  ...propsToFwd
}: InputProps) {
  const customRef: any = React.useRef<
    HTMLInputElement | HTMLTextAreaElement | null
  >(null);

  useScrollIntoView({ scrollIntoView: scrollIntoView, ref: customRef });
  useAutofocus({ autoFocus: autoFocus, ref: customRef, currentValue: value });

  if (inputType === InputModes.date) {
    const { max, min, name } = propsToFwd || {};

    return (
      <DatePicker
        label={label}
        showRequiredAsterisk={showRequiredAsterisk}
        value={value as string}
        onChange={(date) => {
          onChange({
            target: {
              // @ts-ignore
              name: name || "",
              value: date || "",
            },
          });
        }}
        placeholder={placeholder}
        disabled={isDisabled}
        maxDate={max as string}
        minDate={min as string}
        errorText={errorText}
        hasError={hasError}
        required={required}
        helperText={helperText}
        leftIcon={leftIcon}
        rightIcon={rightIcon}
        dateRenderFormat={dateRenderFormat}
      />
    );
  }

  return (
    <div
      className={classNames(s.inputContainer, {
        [containerClass]: !!containerClass,
      })}
    >
      {isStringValid(label) && (
        <div
          className={classNames(s.label, {
            [s.saLabel]: renderType === RenderType.StandAloneInput,
          })}
        >
          <Typography
            color="tertiary"
            label={label}
            variant={
              renderType === RenderType.FormInput
                ? "label_3_medium"
                : "label_2_medium"
            }
          />
          {required && showRequiredAsterisk && (
            <Typography label="*" variant="body_3_medium" color="negative" />
          )}
        </div>
      )}
      {
        {
          [RenderType.FormInput]: (
            <div
              className={classNames(s.input, {
                [s.has_error]: hasError,
                [s.has_help]: isStringValid(helperText),
                [s.is_textarea]: isTextarea,
                [s.disabled]: isDisabled || propsToFwd.disabled,
                [inputClass]: !!inputClass,
              })}
            >
              {leftIcon && <div className={s.left_icon}>{leftIcon}</div>}

              {isTextarea ? (
                <textarea
                  //@ts-ignore
                  ref={(ref) => {
                    if (inputRef) {
                      // @ts-ignore
                      inputRef.current = ref;
                    }
                    customRef.current = ref;
                  }}
                  onChange={onChange}
                  value={value}
                  disabled={isDisabled}
                  placeholder={placeholder}
                  rows={4}
                  {...propsToFwd}
                />
              ) : (
                <input
                  //@ts-ignore
                  ref={(ref) => {
                    if (inputRef) {
                      //@ts-ignore
                      inputRef.current = ref;
                    }
                    customRef.current = ref;
                  }}
                  onChange={onChange}
                  value={value}
                  disabled={isDisabled}
                  placeholder={placeholder}
                  inputMode={inputType}
                  {...propsToFwd}
                />
              )}

              {rightIcon && <div className={s.right_icon}>{rightIcon}</div>}
            </div>
          ),
          [RenderType.StandAloneInput]: (
            <div className={s.standAloneInput}>
              <div
                className={classNames(s.sa_input, {
                  [s.has_error]: hasError,
                  [s.has_help]: isStringValid(helperText),
                  [s.disabled]: isDisabled || propsToFwd.disabled,
                  [inputClass]: !!inputClass,
                })}
              >
                {leftIcon && <div className={s.left_icon}>{leftIcon}</div>}
                <input
                  //@ts-ignore
                  ref={(ref) => {
                    if (inputRef) {
                      // @ts-ignore
                      inputRef.current = ref;
                    }
                    customRef.current = ref;
                  }}
                  onChange={onChange}
                  value={value}
                  disabled={isDisabled}
                  placeholder={placeholder}
                  // @ts-ignore
                  inputMode={inputType}
                  {...propsToFwd}
                />

                {rightIcon && <div className={s.right_icon}>{rightIcon}</div>}
              </div>
            </div>
          ),
        }[renderType]
      }

      <div className={s.textWrapper}>
        {hasError && isStringValid(errorText) && (
          <div className={s.error}>
            <Icon
              name="ShieldWarningRegular"
              width={16}
              height={16}
              color="negative"
            />
            <Typography
              label={errorText}
              variant="label_4_medium"
              color="negative"
              style={{ marginTop: "1px" }}
            />
          </div>
        )}

        {!hasError && isStringValid(helperText) && (
          <div className={s.helperText}>
            <Icon
              name="InfoCircleRegular"
              width={16}
              height={16}
              color="tertiary"
            />
            <Typography
              label={helperText}
              variant="label_4_medium"
              color="tertiary"
              style={{ marginTop: "1px" }}
            />
          </div>
        )}

        {showInputLength && limitCount && (
          <Typography
            label={`${
              validateAndTrimString(`${value}`)?.trimmedStr?.length
            }/${limitCount}`}
            variant="label_4_medium"
            color="tertiary"
            className={s.count}
          />
        )}
      </div>
    </div>
  );
}
